import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';

import styles from './Modal.module.scss';

interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  className?: string;
}

const MODAL_ROOT_ID = 'modal-root';
const EscapeCode = 'Escape';

const Modal = ({ isOpen, onClose, className, children }: React.PropsWithChildren<ModalProps>) => {
  const modalRoot = document.getElementById(MODAL_ROOT_ID);
  const elementRef = useRef<HTMLDivElement>(document.createElement('div'));
  const modalRef = useRef<HTMLDivElement>(null);

  const handleModalOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const modalElement = modalRef.current;

    if (!modalElement) {
      return;
    }

    if (modalElement.contains(event.target as Node)) {
      return;
    }

    if (onClose) {
      onClose();
    }
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) {
        return;
      }

      if (event.code !== EscapeCode) {
        return;
      }

      if (onClose) {
        onClose();
      }
    },
    [isOpen, onClose],
  );

  const renderModalContent = () => {
    if (!isOpen) {
      return null;
    }

    return (
      <div onClick={handleModalOverlayClick} className={styles.modal_overlay}>
        <div ref={modalRef} className={classNames(styles.modal, className)}>
          {children}
        </div>
      </div>
    );
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    const element = elementRef.current;

    if (!modalRoot || !isOpen) {
      return;
    }

    modalRoot.appendChild(element);

    return () => {
      modalRoot.removeChild(element);
    };
  }, [modalRoot, isOpen]);

  return createPortal(renderModalContent(), elementRef.current);
};

export default Modal;
